import React, { useEffect, useState } from "react";
import { Card, Row, Table, TableColumnsType, TableProps, Typography, Popconfirm, Modal, Button } from "antd";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Publication } from "@bindeleddet/core/types";
import axios from "axios";
import { useViewport } from "@bindeleddet/core/hooks";
import MobileTable from "components/MobileTable/MobileTable";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import CreateOrEditPublication from "./CreateOrEditPublication";

const AdminPublications = () => {
  const { Title } = Typography;
  const { width } = useViewport();

  const [publications, setPublications] = useState<Publication[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<Publication | undefined>(undefined);

  useEffect(() => {
    axios
      .get<Publication[]>("/website-contents/publications/")
      .then(response => {
        setPublications(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const getFilteredPublications = () =>
    publications.filter(publication => {
      if (searchTerm === "") {
        return publication;
      }
      if (publication.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return publication;
      }
      return false;
    });

  const filteredPublications =
    publications &&
    getFilteredPublications().map(publication => {
      return publication;
    });

  type OnChange = NonNullable<TableProps<Publication>["onChange"]>;
  type Sorts = {
    columnKey: string;
    order: "ascend" | "descend" | null;
  };

  const [sortedInfo, setSortedInfo] = useState<Sorts>({ columnKey: "publication_date", order: "descend" });

  const handleChange: OnChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const handleCreateOrEdit = (publication: Publication) => {
    if (selectedPublication) {
      setPublications(publications.map(p => (p.id === publication.id ? publication : p)));
    } else {
      setPublications([publication, ...publications]);
    }
    setIsModalOpen(false);
    setSelectedPublication(undefined);
  };

  const columns: TableColumnsType<Publication> = [
    {
      title: "Tittel",
      dataIndex: "title",
      key: "title",
      sorter: (a: Publication, b: Publication) => {
        return a.title.localeCompare(b.title);
      },
      sortOrder: sortedInfo.columnKey === "title" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "publication_type",
      key: "publication_type",
      render: type => {
        return type === 1
          ? "Alumniundersøkelse"
          : type === 2
          ? "Diplomundersøkelse"
          : type === 3
          ? "Covid-19-undersøkelse"
          : "Ukjent";
      },
      ellipsis: true,
    },
    {
      title: "Publiseringsdato",
      dataIndex: "publication_date",
      key: "publication_date",
      sorter: (a: Publication, b: Publication) => {
        const dateA = new Date(a.publication_date);
        const dateB = new Date(b.publication_date);
        return dateA.getTime() - dateB.getTime();
      },
      sortOrder: sortedInfo.columnKey === "publication_date" ? sortedInfo.order : null,
      defaultSortOrder: "descend",
      render: (text, record) => {
        const date = new Date(record.publication_date);
        return date.toLocaleDateString();
      },
      ellipsis: true,
      width: 200,
    },
    {
      key: "edit",
      render: (text, record) => (
        <EditOutlined
          onClick={() => {
            setSelectedPublication(record);
            setIsModalOpen(true);
          }}
          style={{ fontSize: 20, cursor: "pointer" }}
        />
      ),
      width: 50,
    },
    {
      key: "delete",
      render: (text, record) => (
        <Popconfirm
          title={`Slett "${record.title}"`}
          okText="Slett"
          cancelText="Avbryt"
          onConfirm={() => {
            axios
              .delete(`/website-contents/publications/${record.id}/`)
              .then(() => {
                message("success", "Publikasjon slettet");
                setPublications(publications.filter(p => p.id !== record.id));
              })
              .catch(() => message("error", "Kunne ikke slette publikasjon"));
          }}
        >
          <DeleteOutlined style={{ color: "red", fontSize: 20, cursor: "pointer" }} />
        </Popconfirm>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2}>Publikasjoner</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedPublication(undefined);
              setIsModalOpen(true);
            }}
          >
            Opprett ny publikasjon
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            rowKey="id"
            dataSource={filteredPublications}
            loading={loading}
            onChange={handleChange}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Tittel",
                dataIndex: "title",
                key: "title",
              },
              {
                title: "Type",
                dataIndex: "publication_type",
                key: "publication_type",
                render: type => {
                  return type === 1
                    ? "Alumniundersøkelse"
                    : type === 2
                    ? "Diplomundersøkelse"
                    : type === 3
                    ? "Covid-19-undersøkelse"
                    : "Ukjent";
                },
              },
              {
                title: "Publiseringsdato",
                dataIndex: "publication_date",
                key: "publication_date",
                render: date => {
                  return new Date(date as string).toLocaleDateString("nb-NO", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                },
              },
            ]}
            data={filteredPublications}
            loading={loading}
            onClick={id => {
              const publication = publications.find(p => p.id === id);
              if (publication) {
                setSelectedPublication(publication);
                setIsModalOpen(true);
              }
            }}
            emptyText="Ingen publikasjoner funnet"
          />
        )}
      </Card>
      <Modal
        title={<Title level={3}>{selectedPublication ? "Oppdaterer publikasjon" : "Oppretter publikasjon"}</Title>}
        centered
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedPublication(undefined);
        }}
        width="70%"
        footer={null}
        bodyStyle={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)", padding: "20px" }}
      >
        <CreateOrEditPublication
          newPublication={!selectedPublication}
          publication={selectedPublication}
          handleCreateOrEdit={handleCreateOrEdit}
        />
      </Modal>
    </>
  );
};

export default AdminPublications;
